import React, { useEffect, useRef, useState } from 'react';
import api from '../../lib/api';
import { responsiveImg } from '../../lib/imageUrl';
import { ArrowLeft, ArrowRight, Pause, Play } from 'lucide-react';

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [paused, setPaused] = useState(false);
  const scrollerRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/gallery');
        setImages(data);
      } catch { setImages([]); }
    })();
  }, []);

  // Continuous auto-scroll
  useEffect(() => {
    if (paused || images.length === 0 || !scrollerRef.current) return;
    let last = performance.now();
    const SPEED = 45; // px/sec — subtle but visible
    const tick = (now) => {
      const dt = (now - last) / 1000;
      last = now;
      const el = scrollerRef.current;
      if (el) {
        el.scrollLeft += SPEED * dt;
        // loop seamlessly: when past half the duplicated content, reset
        if (el.scrollLeft >= el.scrollWidth / 2) {
          el.scrollLeft -= el.scrollWidth / 2;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused, images]);

  const manualScroll = (dir) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollBy({ left: dir * 380, behavior: 'smooth' });
  };

  if (images.length === 0) return null;

  // Duplicate the images list for seamless looping
  const doubled = [...images, ...images];

  return (
    <section id="gallery" className="pt-6 md:pt-8 pb-32 md:pb-40 bg-[#FFFFFF] overflow-hidden">
      <div className="container-custom mb-8 md:mb-10">
        <div className="flex flex-col md:flex-row justify-end items-start md:items-end gap-8">
          <h2 className="sr-only">Réalisations</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPaused((p) => !p)}
              data-testid="gallery-toggle-autoscroll"
              aria-label={paused ? 'Reprendre' : 'Pause'}
              className="w-12 h-12 flex items-center justify-center border border-[#111111] hover:bg-[#333333] hover:border-[#111111] hover:text-white transition-colors"
            >
              {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            </button>
            <button
              onClick={() => manualScroll(-1)}
              data-testid="gallery-prev"
              aria-label="Précédent"
              className="w-12 h-12 flex items-center justify-center border border-[#111111] hover:bg-[#333333] hover:border-[#111111] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => manualScroll(1)}
              data-testid="gallery-next"
              aria-label="Suivant"
              className="w-12 h-12 flex items-center justify-center border border-[#111111] hover:bg-[#333333] hover:border-[#111111] hover:text-white transition-colors"
            >
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        className="flex gap-5 overflow-x-auto no-scrollbar pl-[5vw] pr-[5vw] pb-6"
        data-testid="gallery-scroller"
      >
        {doubled.map((img, idx) => (
          <article
            key={`${img.id || idx}-${idx}`}
            data-gallery-card
            className="shrink-0 w-[260px] md:w-[300px] group"
          >
            <div className="relative aspect-[3/4] overflow-hidden bg-[#F5F5F5]">
              <img
                {...responsiveImg(img.imageUrl, '(max-width: 768px) 100vw, 33vw')}
                alt={img.title || ''}
                className="w-full h-full object-cover transition-transform duration-[1.5s] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-700" />
            </div>
            <div className="pt-4">
              {img.category && (
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#111111] block mb-1.5">
                  {img.category}
                </span>
              )}
              <h3 className="font-serif text-lg text-[#111111] italic leading-snug">
                {img.title || 'Sans titre'}
              </h3>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default Gallery;
